import React, { useCallback, useEffect, useRef, useState } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, PluginOptionsByType } from "chart.js";
import { _DeepPartialObject } from "chart.js/types/utils";
import { useParams } from "react-router-dom";
import {
    FiActivity,
    FiArrowDownCircle,
    FiArrowUpCircle,
    FiDollarSign,
    FiPieChart,
    FiRefreshCw,
    FiShield,
    FiTrendingUp,
    FiUser,
} from "react-icons/fi";

import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import { LoadingButton } from "../../../components/loadingButtom";
import { ModalDesconectar } from "../../../components/modais/ModalDesconectar";
import { DadosPosicao } from "../../Cliente/Home/Home";
import {
    handleHistoricoFechamento,
    obterServicosFinanceirosCliente,
    servicoFinanceiro,
} from "../../Cliente/Home/functions";
import { usuarioServicoFinanceiroTipo } from "../../../context/servicoFinanceiroCliente";
import useWindowDimensions from "../../../functions/UseWindowDimensions";
import { GraficoLinha } from "../../../components/Graficos/GraficoLinha";
import { GraficoPizza } from "../../../components/Graficos/GraficoPizza";
import "./styles.css";

ChartJS.register(ArcElement, Tooltip, Legend);

const SERVICO_INICIAL: usuarioServicoFinanceiroTipo = {
    id: "",
    idServicoFinanceiro: 0,
    servicoFinanceiro: "",
};

const normalizarServicoFinanceiro = (
    servico: servicoFinanceiro
): usuarioServicoFinanceiroTipo => {
    if (servico.idServicoFinanceiro === 1) {
        return {
            id: servico.id,
            idServicoFinanceiro: 1,
            servicoFinanceiro: "Assessoria",
        };
    }

    if (servico.idServicoFinanceiro === 2) {
        return {
            id: servico.id,
            idServicoFinanceiro: 2,
            servicoFinanceiro: "Custódia",
        };
    }

    if (servico.idServicoFinanceiro === 3) {
        return {
            id: servico.id,
            idServicoFinanceiro: 3,
            servicoFinanceiro: "Private",
        };
    }

    return SERVICO_INICIAL;
};

interface MetricCardProps {
    label: string;
    value: string;
    detail: string;
    icon: React.ReactNode;
    tone?: "dark" | "orange" | "cyan";
}

interface DashboardPanelProps {
    title: string;
    eyebrow: string;
    loading: boolean;
    error: boolean;
    onRetry: () => void;
    children: React.ReactNode;
}

const formatCurrency = (value: number | undefined, isUSD: boolean) => {
    const normalizedValue = typeof value === "number" && Number.isFinite(value) ? value : 0;

    return new Intl.NumberFormat(isUSD ? "en-US" : "pt-BR", {
        style: "currency",
        currency: isUSD ? "USD" : "BRL",
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    }).format(normalizedValue);
};

const formatPercent = (value: number | undefined) => {
    const normalizedValue = typeof value === "number" && Number.isFinite(value) ? value : 0;

    return `${new Intl.NumberFormat("pt-BR", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    }).format(normalizedValue)}%`;
};

const MetricCard = ({ label, value, detail, icon, tone = "dark" }: MetricCardProps) => (
    <article className={`gestor-client-metric ${tone}`}>
        <span className="gestor-client-metric-icon" aria-hidden="true">
            {icon}
        </span>
        <div>
            <p>{label}</p>
            <strong>{value}</strong>
            <small>{detail}</small>
        </div>
    </article>
);

const EmptyState = ({
    message,
    onRetry,
}: {
    message: string;
    onRetry: () => void;
}) => (
    <div className="gestor-client-state error">
        <strong>{message}</strong>
        <button type="button" onClick={onRetry}>
            <FiRefreshCw />
            Tentar novamente
        </button>
    </div>
);

const DashboardPanel = ({
    title,
    eyebrow,
    loading,
    error,
    onRetry,
    children,
}: DashboardPanelProps) => (
    <section className="gestor-client-panel">
        <div className="gestor-client-panel-header">
            <div>
                <span>{eyebrow}</span>
                <h2>{title}</h2>
            </div>
            {error && (
                <button type="button" onClick={onRetry}>
                    <FiRefreshCw />
                    Recarregar
                </button>
            )}
        </div>

        {loading ? (
            <div className="gestor-client-state">
                <LoadingButton />
            </div>
        ) : error ? (
            <EmptyState message="Não foi possível carregar estes dados." onRetry={onRetry} />
        ) : (
            children
        )}
    </section>
);

export const DashboardsDoCliente = () => {
    const [modalDesconectar, setModalDesconectar] = useState<boolean>(false);
    const [dadosPosicao, setDadosPosicao] = useState<DadosPosicao>();
    const [loadingPosicao, setLoadingPosicao] = useState<boolean>(true);
    const [errorPosicao, setErrorPosicao] = useState<boolean>(false);
    const [dataInicial] = useState<string>(
        new Date(new Date().setMonth(new Date().getMonth() - 72)).toISOString()
    );
    const [dataFinal] = useState<string>(new Date().toISOString());
    const [rendimentosDia, setRendimentosDia] = useState<Array<number>>([]);
    const [valoresTotais, setValoresTotais] = useState<Array<number>>([]);
    const [valoresTotaisPrivate, setValoresTotaisPrivate] = useState<Array<number>>([]);
    const [valoresAcumulados, setValoresAcumulados] = useState<Array<number>>([]);
    const [labels, setLabels] = useState<Array<string>>([]);
    const [indexData, setIndexData] = useState<number>(0);
    const [indexDataPrivate, setIndexDataPrivate] = useState<number>(0);
    const [colorArray, setColorArray] = useState<Array<string>>([]);
    const [porcentagem, setPorcentagem] = useState<Array<number>>([]);
    const [, setValorTotal] = useState<Array<number>>([]);
    const [, setValorUnitario] = useState<Array<number>>([]);
    const [labelsMoeda, setLabelsMoeda] = useState<Array<string>>([]);
    const [loadingGrafico, setLoadingGrafico] = useState<boolean>(true);
    const [errorGrafico, setErrorGrafico] = useState<boolean>(false);
    const [listaServicosFinanceiros, setListaServicoFinanceiros] =
        useState<servicoFinanceiro[]>([]);
    const [erroSelect, setErroSelect] = useState<boolean>(false);
    const [servicoFinanceiroCliente, setServicoFinanceiroCliente] =
        useState<usuarioServicoFinanceiroTipo>(SERVICO_INICIAL);
    const [idServicoFinanceiroCliente, setIdServicoFinanceiroCliente] = useState<number>(0);
    const primeiraTrocaServico = useRef<boolean>(true);

    const { height, width } = useWindowDimensions();
    const { idCliente, nome } = useParams();

    const clienteNome = nome || "Cliente";
    const isUSD = idServicoFinanceiroCliente === 3;
    const isPrivate = [2, 3].includes(idServicoFinanceiroCliente);
    const serviceName = servicoFinanceiroCliente.servicoFinanceiro || "Serviço financeiro";

    const limparSeries = useCallback(() => {
        setValoresAcumulados([]);
        setRendimentosDia([]);
        setValoresTotais([]);
        setValoresTotaisPrivate([]);
        setLabels([]);
        setLabelsMoeda([]);
        setPorcentagem([]);
        setColorArray([]);
    }, []);

    const carregarHistorico = useCallback(
        (servicoSelecionado = servicoFinanceiroCliente) => {
            if (!servicoSelecionado.idServicoFinanceiro) {
                return;
            }

            limparSeries();
            handleHistoricoFechamento(
                setLoadingGrafico,
                setErrorGrafico,
                dataInicial,
                dataFinal,
                setModalDesconectar,
                setRendimentosDia,
                setValoresTotais,
                setLabels,
                setValoresAcumulados,
                servicoSelecionado,
                setLoadingPosicao,
                setErrorPosicao,
                setDadosPosicao,
                setLabelsMoeda,
                setPorcentagem,
                setValorUnitario,
                setValorTotal,
                [],
                setColorArray,
                setValoresTotaisPrivate,
                idCliente
            );
        },
        [dataFinal, dataInicial, idCliente, limparSeries, servicoFinanceiroCliente]
    );

    const carregarServicos = useCallback(() => {
        if (!idCliente) {
            return;
        }

        obterServicosFinanceirosCliente(
            idCliente,
            setListaServicoFinanceiros,
            setErroSelect,
            setModalDesconectar,
            setServicoFinanceiroCliente,
            setIdServicoFinanceiroCliente,
            setLoadingGrafico,
            setErrorGrafico,
            dataInicial,
            dataFinal,
            setRendimentosDia,
            setValoresTotais,
            setLabels,
            setValoresAcumulados,
            SERVICO_INICIAL,
            setLoadingPosicao,
            setErrorPosicao,
            setDadosPosicao,
            setLabelsMoeda,
            setPorcentagem,
            setValorUnitario,
            setValorTotal,
            [],
            setColorArray,
            setValoresTotaisPrivate,
            idCliente
        );
    }, [dataFinal, dataInicial, idCliente]);

    const handleReload = () => {
        if (servicoFinanceiroCliente.idServicoFinanceiro) {
            carregarHistorico();
            return;
        }

        carregarServicos();
    };

    useEffect(() => {
        carregarServicos();
    }, [carregarServicos]);

    useEffect(() => {
        if (idServicoFinanceiroCliente === 0) {
            return;
        }

        if (primeiraTrocaServico.current) {
            primeiraTrocaServico.current = false;
            return;
        }

        carregarHistorico();
    }, [carregarHistorico, idServicoFinanceiroCliente]);

    const plugins: _DeepPartialObject<PluginOptionsByType<"pie">> = {
        legend: {
            display: true,
            position: height >= width ? "top" : "left",
        },
        tooltip: {
            enabled: true,
            mode: "point",
            callbacks: {
                title: (data) => data.map((data2) => data2.label),
                label: (data) => [
                    `Participação na carteira: ${formatPercent(porcentagem[data.dataIndex])}`,
                ],
            },
        },
    };

    const rentabilidade = dadosPosicao?.totalAportes
        ? (((dadosPosicao.saldoCliente + dadosPosicao.totalSaques * -1) /
            dadosPosicao.totalAportes) -
            1) *
        100
        : 0;

    const positionMetrics = [
        {
            label: "Saldo líquido",
            value: formatCurrency(dadosPosicao?.saldoCliente, isUSD),
            detail: isUSD ? "Consolidado em dólar" : "Consolidado em reais",
            icon: <FiDollarSign />,
            tone: "dark" as const,
        },
        {
            label: "Rentabilidade",
            value: formatPercent(rentabilidade),
            detail: "Resultado acumulado",
            icon: <FiTrendingUp />,
            tone: "cyan" as const,
        },
        {
            label: "Total de aportes",
            value: formatCurrency(dadosPosicao?.totalAportes, isUSD),
            detail: "Capital alocado",
            icon: <FiArrowDownCircle />,
            tone: "orange" as const,
        },
        {
            label: "Total de saques",
            value: formatCurrency(dadosPosicao?.totalSaques, isUSD),
            detail: "Retiradas realizadas",
            icon: <FiArrowUpCircle />,
        },
    ];

    const quotaMetrics = [
        {
            label: "Cotas",
            value: dadosPosicao?.cotas.toString().match(/^-?\d+(?:\.\d{0,4})?/)?.[0] || "0",
            detail: "Quantidade em carteira",
            icon: <FiPieChart />,
        },
        {
            label: "Valor da cota",
            value: formatCurrency(dadosPosicao?.valorCota, false),
            detail: "Referência atual",
            icon: <FiActivity />,
        },
    ];

    const selecionarServico = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selecionado = listaServicosFinanceiros.find(
            (servico) => servico.id === event.target.value
        );

        if (!selecionado) {
            return;
        }

        const servicoNormalizado = normalizarServicoFinanceiro(selecionado);

        setServicoFinanceiroCliente(servicoNormalizado);
        setIdServicoFinanceiroCliente(servicoNormalizado.idServicoFinanceiro);
    };

    return (
        <>
            <Navbar />
            <main id="container" className="gestor-client-dashboard-page">
                <section className="gestor-client-hero" aria-labelledby="gestor-client-title">
                    <div className="gestor-client-hero-copy">
                        <span>Dashboard do cliente</span>
                        <h1 id="gestor-client-title">{clienteNome}</h1>
                        <p>
                            Visão consolidada para acompanhar posição, evolução de performance
                            e composição da carteira dentro da operação Omex Capital.
                        </p>
                    </div>

                    <aside className="gestor-client-control-panel" aria-label="Controle do dashboard">
                        <div>
                            <FiUser />
                            <span>Cliente</span>
                            <strong>{clienteNome}</strong>
                        </div>

                        <label>
                            <span>Serviço financeiro</span>
                            <select
                                value={servicoFinanceiroCliente.id}
                                onChange={selecionarServico}
                                disabled={!listaServicosFinanceiros.length || loadingGrafico}
                            >
                                {listaServicosFinanceiros.length ? (
                                    listaServicosFinanceiros.map((servico) => (
                                        <option key={servico.id} value={servico.id}>
                                            {servico.servicoFinanceiro}
                                        </option>
                                    ))
                                ) : (
                                    <option value="">Carregando serviços</option>
                                )}
                            </select>
                        </label>

                        {erroSelect && <p className="gestor-client-select-error">Serviços indisponíveis.</p>}

                        <button type="button" onClick={handleReload} disabled={loadingGrafico}>
                            <FiRefreshCw />
                            Atualizar dados
                        </button>
                    </aside>
                </section>

                <section className="gestor-client-context-grid" aria-label="Contexto do cliente">
                    <article>
                        <span>Serviço ativo</span>
                        <strong>{serviceName}</strong>
                    </article>
                    <article>
                        <span>Moeda base</span>
                        <strong>{isUSD ? "USD" : "BRL"}</strong>
                    </article>
                    <article>
                        <span>Histórico carregado</span>
                        <strong>{labels.length}</strong>
                    </article>
                    <article>
                        <span>Ativos na composição</span>
                        <strong>{labelsMoeda.length}</strong>
                    </article>
                </section>

                {errorPosicao ? (
                    <section className="gestor-client-panel">
                        <EmptyState
                            message="Não foi possível carregar a posição consolidada."
                            onRetry={handleReload}
                        />
                    </section>
                ) : loadingPosicao ? (
                    <section className="gestor-client-panel">
                        <div className="gestor-client-state">
                            <LoadingButton />
                        </div>
                    </section>
                ) : (
                    <section className="gestor-client-metric-grid" aria-label="Posição consolidada">
                        {!isUSD && quotaMetrics.map((metric) => (
                            <MetricCard key={metric.label} {...metric} />
                        ))}
                        {positionMetrics.map((metric) => (
                            <MetricCard key={metric.label} {...metric} />
                        ))}
                    </section>
                )}

                {idServicoFinanceiroCliente !== 0 && !isUSD && (
                    <section className="gestor-client-rules-panel" aria-labelledby="rules-title">
                        <div className="gestor-client-panel-header">
                            <div>
                                <span>Regras operacionais</span>
                                <h2 id="rules-title">Informações gerais</h2>
                            </div>
                            <FiShield />
                        </div>

                        <div className="gestor-client-rules-grid">
                            <article>
                                <span>Cota de aplicação</span>
                                <strong>D+1</strong>
                            </article>
                            <article>
                                <span>Cota de resgate</span>
                                <strong>D+2</strong>
                            </article>
                            <article>
                                <span>Pagamento do resgate</span>
                                <strong>D+5</strong>
                                <small>Após cotização</small>
                            </article>
                            <article>
                                <span>Aplicação inicial mínima</span>
                                <strong>R$ 25.000,00</strong>
                            </article>
                        </div>
                    </section>
                )}

                <section className="gestor-client-chart-grid" aria-label="Gráficos de performance">
                    {!isPrivate ? (
                        <>
                            <DashboardPanel
                                title="Performance mensal da assessoria Omex Capital"
                                eyebrow="Evolução"
                                loading={loadingGrafico}
                                error={errorGrafico}
                                onRetry={handleReload}
                            >
                                <GraficoLinha
                                    setIndexData={setIndexData}
                                    dadosGrafico={valoresTotais}
                                    labels={labels}
                                    alturaGrafico={height >= width ? "350px" : "90%"}
                                    nomeDado="Evolução"
                                    tituloGrafico="Performance mensal da assessoria Omex Capital"
                                    tituloTooltip="Diferença percentual mensal"
                                    dadoTooltip={`Valor percentual: ${formatPercent(
                                        valoresTotais[indexData]
                                    )}`}
                                    graficoPorcentagem
                                />
                            </DashboardPanel>

                            <DashboardPanel
                                title="Rendimento acumulado da assessoria Omex Capital"
                                eyebrow="Acumulado"
                                loading={loadingGrafico}
                                error={errorGrafico}
                                onRetry={handleReload}
                            >
                                <GraficoLinha
                                    setIndexData={setIndexDataPrivate}
                                    dadosGrafico={valoresAcumulados}
                                    labels={labels}
                                    alturaGrafico={height >= width ? "350px" : "90%"}
                                    nomeDado="Evolução"
                                    tituloGrafico="Rendimento acumulado da assessoria Omex Capital"
                                    tituloTooltip="Valor acumulado percentual"
                                    dadoTooltip={`Valor acumulado: ${formatPercent(
                                        valoresAcumulados[indexDataPrivate]
                                    )}`}
                                    graficoPorcentagem
                                />
                            </DashboardPanel>
                        </>
                    ) : (
                        <>
                            <DashboardPanel
                                title="Performance mensal Private Omex Capital"
                                eyebrow="Evolução"
                                loading={loadingGrafico}
                                error={errorGrafico}
                                onRetry={handleReload}
                            >
                                <GraficoLinha
                                    setIndexData={setIndexData}
                                    dadosGrafico={valoresTotaisPrivate}
                                    labels={labels}
                                    alturaGrafico={height >= width ? "350px" : "90%"}
                                    nomeDado="Evolução"
                                    tituloGrafico="Performance mensal Private Omex Capital"
                                    tituloTooltip="Diferença percentual mensal"
                                    dadoTooltip={`Valor percentual: ${formatPercent(
                                        valoresTotaisPrivate[indexData]
                                    )}`}
                                    graficoPorcentagem
                                />
                            </DashboardPanel>

                            <DashboardPanel
                                title="Percentual acumulado Private Omex Capital"
                                eyebrow="Acumulado"
                                loading={loadingGrafico}
                                error={errorGrafico}
                                onRetry={handleReload}
                            >
                                <GraficoLinha
                                    setIndexData={setIndexDataPrivate}
                                    dadosGrafico={rendimentosDia || [0]}
                                    labels={labels}
                                    alturaGrafico={height >= width ? "350px" : "90%"}
                                    nomeDado="Evolução"
                                    tituloGrafico="Percentual acumulado Private Omex Capital"
                                    tituloTooltip="Percentual acumulado"
                                    dadoTooltip={`Percentual acumulado: ${formatPercent(
                                        rendimentosDia[indexDataPrivate]
                                    )}`}
                                    graficoPorcentagem
                                />
                            </DashboardPanel>
                        </>
                    )}
                </section>

                <DashboardPanel
                    title="Composição da carteira"
                    eyebrow="Alocação"
                    loading={loadingGrafico}
                    error={errorGrafico}
                    onRetry={handleReload}
                >
                    <GraficoPizza
                        colorArray={colorArray}
                        labels={labelsMoeda}
                        porcentagens={porcentagem}
                        plugins={plugins}
                        tituloGrafico="Composição da carteira"
                        subTituloGrafico="Moedas com menos de 1% de participação foram ocultadas"
                        mobile={height >= width}
                    />
                </DashboardPanel>
            </main>
            <Footer />
            {modalDesconectar && <ModalDesconectar modal={modalDesconectar} />}
        </>
    );
};
